/**
 * API Configuration
 * Centralized configuration for all backend API calls
 */

// Backend API base URL
export const API_BASE_URL = 'https://xrepl.app';

// API Endpoints
export const API_ENDPOINTS = {
  // Authentication
  AUTH_REGISTER: `${API_BASE_URL}/api/auth/register`,
  AUTH_GOOGLE_REGISTER: `${API_BASE_URL}/api/auth/google-register`,
  AUTH_LINKEDIN_REGISTER: `${API_BASE_URL}/api/auth/linkedin-register`,
  AUTH_DELETE_ACCOUNT: `${API_BASE_URL}/api/auth/delete-account`,
  
  // User
  USER_PROFILE: `${API_BASE_URL}/api/user/profile`,
  USAGE_INFO: `${API_BASE_URL}/api/usage-info`,
  PACKAGES_LIST: `${API_BASE_URL}/api/packages/list`,
  PACKAGES_DEFINITIONS: `${API_BASE_URL}/api/packages/definitions`,

  // Plans (pricing page: monthly/annual from plans table)
  PLANS_LIST: `${API_BASE_URL}/api/plans/list`,

  // Subscriptions (free plan is via OAuth only - auth/register creates user + free license)
  SUBSCRIBE_CHECKOUT: `${API_BASE_URL}/api/subscribe/checkout`,
  SUBSCRIBE_CHECKOUT_PLAN_CHANGE: `${API_BASE_URL}/api/subscribe/checkout-plan-change`,
  SUBSCRIBE_SESSION_SUCCESS: `${API_BASE_URL}/api/subscribe/session-success`,
  SUBSCRIBE_PLAN_STATUS: `${API_BASE_URL}/api/subscribe/plan-status`,
  SUBSCRIBE_UPDATE_PLAN: `${API_BASE_URL}/api/subscribe/update-plan`,
  SUBSCRIBE_CANCEL: `${API_BASE_URL}/api/subscribe/cancel`,
  SUBSCRIBE_REACTIVATE: `${API_BASE_URL}/api/subscribe/reactivate`,

  // Package management (add-ons)
  PACKAGES_ADD: `${API_BASE_URL}/api/packages/add`,
  PACKAGES_REMOVE: `${API_BASE_URL}/api/packages/remove`,
  PACKAGES_PREVIEW_ADD: `${API_BASE_URL}/api/packages/preview-add`,

  // Plan change with preview (subscription.update approach)
  SUBSCRIBE_PREVIEW_PLAN_CHANGE: `${API_BASE_URL}/api/subscribe/preview-plan-change`,
  SUBSCRIBE_UPDATE_PLAN_DIRECT: `${API_BASE_URL}/api/subscribe/update-plan-direct`,

  // Maintenance Mode
  MAINTENANCE: `${API_BASE_URL}/api/maintenance`,

  // Waitlist
  WAITLIST: `${API_BASE_URL}/api/waitlist`,

  // Referrals
  REFERRALS: `${API_BASE_URL}/api/referrals`,

  // User Preferences
  USER_PREFERENCES: `${API_BASE_URL}/api/user/preferences`,

  // Contact
  CONTACT: `${API_BASE_URL}/api/contact`,
  CONTACT_SALES: `${API_BASE_URL}/api/contact-sales`,

  // Chat (Saxa)
  SALES_CHAT: `${API_BASE_URL}/api/sales-chat`,

  // Admin (session cookie; use credentials: 'include')
  ADMIN_LOGIN: `${API_BASE_URL}/api/admin/login`,
  ADMIN_LOGIN_APP_PASSWORD: `${API_BASE_URL}/api/admin/login-app-password`,
  ADMIN_LOGOUT: `${API_BASE_URL}/api/admin/logout`,
  ADMIN_ME: `${API_BASE_URL}/api/admin/me`,
  ADMIN_SETTINGS: `${API_BASE_URL}/api/admin/settings`,
  ADMIN_PLANS: `${API_BASE_URL}/api/admin/plans`,
  ADMIN_PLANS_ID: (id: string) => `${API_BASE_URL}/api/admin/plans/${id}`,
  ADMIN_PLANS_OPTIONS: `${API_BASE_URL}/api/admin/plans/options`,
  ADMIN_PACKAGES: `${API_BASE_URL}/api/admin/packages`,
  ADMIN_PACKAGES_ID: (id: string) => `${API_BASE_URL}/api/admin/packages/${id}`,
  ADMIN_PACKAGE_ELIGIBILITY: `${API_BASE_URL}/api/admin/package-eligibility`,
  ADMIN_ADMIN_USERS: `${API_BASE_URL}/api/admin/admin-users`,
  ADMIN_ADMIN_USERS_REVOKE: `${API_BASE_URL}/api/admin/admin-users/revoke`,
  // Administration
  ADMIN_USERS: `${API_BASE_URL}/api/admin/users`,
  ADMIN_USERS_ID: (id: string) => `${API_BASE_URL}/api/admin/users/${id}`,
  ADMIN_LICENSES: `${API_BASE_URL}/api/admin/licenses`,
  ADMIN_LICENSES_ID: (id: string) => `${API_BASE_URL}/api/admin/licenses/${id}`,
  ADMIN_USAGE_TODAY: `${API_BASE_URL}/api/admin/usage/today`,
  ADMIN_USAGE_RANGE: `${API_BASE_URL}/api/admin/usage/range`,
  ADMIN_USAGE_AGGREGATE: `${API_BASE_URL}/api/admin/usage/aggregate`,
  // AI Integration (providers, models, cron sync toggle)
  ADMIN_AI_INTEGRATION: `${API_BASE_URL}/api/admin/ai-integration`,
  ADMIN_PROVIDER_SYNC_MODELS: (id: string) => `${API_BASE_URL}/api/admin/providers/${id}/sync-models`,
  ADMIN_PROVIDER_API_KEY: (id: string) => `${API_BASE_URL}/api/admin/providers/${id}/api-key`,
  ADMIN_MODEL_ID: (id: string) => `${API_BASE_URL}/api/admin/models/${id}`,
} as const;

/**
 * Helper function to make API calls with proper error handling
 */
export async function apiCall<T = any>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  try {
    const response = await fetch(endpoint, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API call error:', error);
    throw error;
  }
}

/** Admin API: use credentials so session cookie is sent */
export async function adminApiCall<T = any>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const response = await fetch(endpoint, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(options?.headers as Record<string, string>),
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data as { error?: string })?.error || response.statusText);
  }
  return data as T;
}
